import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Fab,
  Avatar,
  CircularProgress,
  Tooltip,
  Badge,
  Drawer,
  Divider,
  useMediaQuery,
  useTheme,
  Collapse,
  Fade,
  Zoom,
  ButtonBase,
  Chip,
} from "@mui/material";
import {
  ChatRounded as ChatIcon,
  Close as CloseIcon,
  Send as SendIcon,
  SupportAgent as SupportIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  AccessTime as TimeIcon,
  EmojiEmotions as EmojiIcon,
  AttachFile as AttachIcon,
  MoreVert as MoreIcon,
  Minimize as MinimizeIcon,
  VolumeUp as SoundIcon,
  VolumeOff as MuteIcon,
} from "@mui/icons-material";

const API_URL =
  process.env.NODE_ENV === "production" ? "/api" : "http://localhost:3001/api";

// Common quick responses
const QUICK_RESPONSES = [
  "Jag behöver hjälp med min beställning",
  "Hur återbetalar jag en produkt?",
  "När kommer min leverans?",
  "Finns produkten i lager?",
];

// Message notification sound
const MESSAGE_SOUND = new Audio("/sounds/message.mp3"); // Assuming you have a sound file

const SupportWidget = ({ showSnackbar, user }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [message, setMessage] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandQuickResponses, setExpandQuickResponses] = useState(false);
  const [isSoundEnabled, setIsSoundEnabled] = useState(true);
  const [typingIndicator, setTypingIndicator] = useState(false);
  const [messages, setMessages] = useState([]);
  const [agentStatus, setAgentStatus] = useState("online"); // online, away, offline
  const [agentName, setAgentName] = useState("Support");
  const [hasInitialMessage, setHasInitialMessage] = useState(false);

  const widgetRef = useRef(null);
  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const messageEndRef = useRef(null);

  // Initialize chat with welcome message after a brief delay (simulates agent joining)
  // Initialize chat with welcome message after a brief delay (simulates agent joining)
  useEffect(() => {
    if (!hasInitialMessage && isOpen) {
      // Add the initial message immediately when opening
      setMessages([
        {
          id: Date.now(),
          text: "Skriv ett meddelande så hör vi av oss via e-post inom kort!",
          sender: "support",
          timestamp: new Date(),
          status: "sent",
          agentName,
        },
      ]);
      setHasInitialMessage(true);
      // No need for typing indicator or sound for initial load
    }
  }, [isOpen, hasInitialMessage, agentName]);

  // Handle notifications and badge count
  useEffect(() => {
    if (
      !isOpen &&
      messages.length > 0 &&
      messages[messages.length - 1].sender === "support"
    ) {
      setUnreadCount((prev) => prev + 1);
      // Could also trigger browser notification here if permissions granted
    }
  }, [messages, isOpen]);

  // Reset unread count when opening
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Scroll to bottom when messages change
  const scrollToBottom = useCallback(() => {
    if (messageEndRef.current) {
      messageEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (isOpen && !isMinimized) {
      scrollToBottom();
    }
  }, [messages, isOpen, isMinimized, scrollToBottom]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        if (isOpen && !isMobile) {
          // Completely close the widget instead of minimizing
          setIsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, isMobile]);

  // Focus input when opening the chat
  useEffect(() => {
    if (isOpen && !isMinimized && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 300);
    }
  }, [isOpen, isMinimized]);

  // Play message sound effect
  const playMessageSound = () => {
    if (isSoundEnabled) {
      MESSAGE_SOUND.play().catch(() => {
        // Handle autoplay restrictions
        console.log("Sound playback was prevented");
      });
    }
  };

  // Format timestamp to local time
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Handle send message with improved flow and timing
  const handleSendMessage = async (e, quickResponse = null) => {
    e?.preventDefault();

    const messageText = quickResponse || message.trim();
    if (!messageText) return;

    const userMessage = {
      id: Date.now(),
      text: messageText,
      sender: "user",
      timestamp: new Date(),
      status: "sending",
    };

    // Add user message to chat
    setMessages((prev) => [...prev, userMessage]);
    setMessage("");
    setIsSending(true);
    setExpandQuickResponses(false);

    // Scroll to bottom immediately when message is sent
    setTimeout(scrollToBottom, 50);

    // Update message status to sent after a brief delay
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === userMessage.id ? { ...msg, status: "sent" } : msg,
        ),
      );
    }, 500);

    try {
      // Show typing indicator IMMEDIATELY after user sends message
      setTypingIndicator(true);
      scrollToBottom();

      // Send email in the background
      const response = await fetch(`${API_URL}/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: user?.displayName || "Anonym användare",
          email: user?.email || "",
          message: messageText,
        }),
      });

      if (response.ok) {
        // Add system response after a delay to simulate agent typing
        setTimeout(() => {
          setTypingIndicator(false);

          // Show message immediately after typing indicator disappears
          setMessages((prev) => [
            ...prev,
            {
              id: Date.now(),
              text: "Ditt meddelande har skickats & vi återkommer via e-post inom kort.",
              sender: "support",
              timestamp: new Date(),
              status: "sent",
              agentName,
            },
          ]);
          playMessageSound();
          setIsSending(false);
          setTimeout(scrollToBottom, 50);
        }, 2000);
      } else {
        throw new Error("Failed to send");
      }
    } catch (error) {
      showSnackbar("Något gick fel. Försök igen senare.", "error");
      setIsSending(false);

      // Update message status to error
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === userMessage.id ? { ...msg, status: "error" } : msg,
        ),
      );
    }
  };

  // Handle quick response selection
  const handleQuickResponse = (response) => {
    handleSendMessage(null, response);
  };

  // Toggle widget open state
  const toggleWidget = () => {
    if (isOpen) {
      // Always completely close the widget instead of minimizing
      setIsOpen(false);
      // Reset minimized state for next open
      setIsMinimized(false);
    } else {
      setIsOpen(true);
    }
  };

  // Render message status icon
  const renderMessageStatus = (status) => {
    switch (status) {
      case "sending":
        return <CircularProgress size={12} sx={{ ml: 0.5 }} />;
      case "sent":
        return (
          <CheckIcon
            fontSize="small"
            sx={{ fontSize: 12, ml: 0.5, color: "#8e8e8e" }}
          />
        );
      case "delivered":
        return (
          <CheckIcon
            fontSize="small"
            sx={{ fontSize: 12, ml: 0.5, color: theme.palette.primary.main }}
          />
        );
      case "error":
        return (
          <TimeIcon
            fontSize="small"
            sx={{ fontSize: 12, ml: 0.5, color: theme.palette.error.main }}
          />
        );
      default:
        return null;
    }
  };

  // Custom tooltip for the agent status
  const AgentStatusIndicator = () => {
    const statusColors = {
      online: "#4caf50", // Green
      away: "#ff9800", // Orange
      offline: "#bdbdbd", // Grey
    };

    const statusText = {
      online: "Online",
      away: "Away",
      offline: "Offline",
    };

    return (
      <Tooltip title={statusText[agentStatus]} arrow>
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            bgcolor: statusColors[agentStatus],
            boxShadow: "0 0 0 3px white",
            position: "absolute",
            bottom: 3,
            right: 3,
          }}
        />
      </Tooltip>
    );
  };

  const renderChatWidget = () => {
    const ChatBox = isMobile ? Drawer : Paper;
    const chatBoxProps = isMobile
      ? {
          anchor: "bottom",
          open: isOpen,
          onClose: () => setIsOpen(false),
          sx: {
            "& .MuiDrawer-paper": {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              height: "calc(100% - 80px)",
              maxHeight: "600px",
            },
          },
        }
      : {
          elevation: 8,
          sx: {
            position: "absolute",
            bottom: 16,
            right: 0,
            width: 380,
            height: isMinimized ? "auto" : 520,
            maxHeight: isMinimized ? 60 : "80vh",
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
            transition: theme.transitions.create(["height", "max-height"], {
              duration: theme.transitions.duration.standard,
            }),
            animation: "fadeIn 0.3s ease-out",
            "@keyframes fadeIn": {
              "0%": {
                opacity: 0,
                transform: "translateY(20px)",
              },
              "100%": {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        };

    return (
      <ChatBox {...chatBoxProps}>
        {/* Header */}
        <Box
          sx={{
            bgcolor: "primary.main",
            background: isMinimized
              ? "linear-gradient(90deg, #3f51b5 0%, #5c6bc0 100%)"
              : "linear-gradient(90deg, #3f51b5 0%, #5c6bc0 100%)",
            color: "primary.contrastText",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: isMinimized ? "pointer" : "default",
            borderBottom: isMinimized
              ? "none"
              : `1px solid ${theme.palette.divider}`,
          }}
          onClick={isMinimized ? toggleWidget : undefined}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: "primary.dark",
                  border: "2px solid white",
                }}
                src="/support-agent.jpg" // Optional real agent photo
              >
                <SupportIcon />
              </Avatar>
              <AgentStatusIndicator />
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight="600">
                {agentName}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {agentStatus === "online"
                  ? "Aktiv nu"
                  : agentStatus === "away"
                    ? "Borta för tillfället"
                    : "Offline"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 0.5 }}>
            {!isMinimized && <></>}

            <IconButton
              color="inherit"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                // Always completely close the widget, not just minimize
                setIsOpen(false);
              }}
            >
              {isMinimized ? <ExpandMoreIcon /> : <CloseIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Minimized state stops here */}
        {!isMinimized && (
          <>
            {/* Chat Messages */}
            <Box
              ref={chatContainerRef}
              sx={{
                flex: 1,
                overflowY: "auto",
                p: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                bgcolor: "#f8fafc",
                backgroundImage:
                  "radial-gradient(circle at center top, #f0f4f8 0%, rgba(255,255,255,0) 70%)",
              }}
            >
              {messages.map((msg, index) => (
                <Box
                  key={msg.id}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems:
                      msg.sender === "user" ? "flex-end" : "flex-start",
                    mb: 1,
                    opacity: 1,
                    transform: "translateY(0px)",
                    animation:
                      index === messages.length - 1
                        ? "fadeInUp 0.4s ease-out"
                        : "none",
                    "@keyframes fadeInUp": {
                      "0%": {
                        opacity: 0,
                        transform: "translateY(10px)",
                      },
                      "100%": {
                        opacity: 1,
                        transform: "translateY(0px)",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-end", mb: 0.5 }}
                  >
                    {msg.sender === "support" && (
                      <Avatar
                        src="/support-agent.jpg"
                        sx={{
                          width: 28,
                          height: 28,
                          bgcolor: "primary.main",
                          mr: 1,
                          mb: 0.5,
                        }}
                      >
                        <SupportIcon sx={{ fontSize: 16 }} />
                      </Avatar>
                    )}
                    <Box
                      sx={{
                        maxWidth: msg.sender === "user" ? "85%" : "85%",
                        minWidth: msg.sender === "user" ? "180px" : "auto",
                        p: 1.5,
                        borderRadius: 2.5,
                        bgcolor:
                          msg.sender === "user" ? "primary.main" : "white",
                        color: msg.sender === "user" ? "white" : "text.primary",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                        borderBottomRightRadius:
                          msg.sender === "user" ? 0 : 2.5,
                        borderBottomLeftRadius:
                          msg.sender === "support" ? 0 : 2.5,
                        position: "relative",
                        "&::after":
                          msg.sender === "user"
                            ? {
                                content: '""',
                                position: "absolute",
                                bottom: 0,
                                right: -6,
                                width: 12,
                                height: 12,
                                backgroundColor: "primary.main",
                                clipPath: "polygon(0 0, 0% 100%, 100% 100%)",
                              }
                            : msg.sender === "support"
                              ? {
                                  content: '""',
                                  position: "absolute",
                                  bottom: 0,
                                  left: -6,
                                  width: 12,
                                  height: 12,
                                  backgroundColor: "white",
                                  clipPath:
                                    "polygon(100% 0, 0% 100%, 100% 100%)",
                                }
                              : {},
                      }}
                    >
                      {msg.sender === "support" && (
                        <Typography
                          variant="caption"
                          component="div"
                          color="primary.main"
                          fontWeight={500}
                          sx={{ mb: 0.5 }}
                        >
                          {msg.agentName}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {msg.text}
                      </Typography>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent:
                        msg.sender === "user" ? "flex-end" : "flex-start",
                      px: 1,
                      gap: 0.5,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontSize: "0.7rem" }}
                    >
                      {formatTime(msg.timestamp)}
                    </Typography>
                    {msg.sender === "user" && renderMessageStatus(msg.status)}
                  </Box>
                </Box>
              ))}

              {/* Typing indicator */}
              {/* Typing indicator with fade in/out animation */}
              <Fade in={typingIndicator} timeout={{ enter: 500, exit: 300 }}>
                <Box
                  sx={{
                    display: typingIndicator ? "flex" : "none",
                    alignItems: "flex-end",
                    mb: 1,
                    opacity: typingIndicator ? 1 : 0,
                  }}
                >
                  <Avatar
                    src="/support-agent.jpg"
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: "primary.main",
                      mr: 1,
                      mb: 0.5,
                    }}
                  >
                    <SupportIcon sx={{ fontSize: 16 }} />
                  </Avatar>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      bgcolor: "white",
                      color: "text.secondary",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                      borderBottomLeftRadius: 0,
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: 0,
                        left: -6,
                        width: 12,
                        height: 12,
                        backgroundColor: "white",
                        clipPath: "polygon(100% 0, 0% 100%, 100% 100%)",
                      },
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: "60px",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "text.secondary",
                          animation: "bounce 1.4s infinite ease-in-out both",
                          animationDelay: "0s",
                          "@keyframes bounce": {
                            "0%, 100%": {
                              transform: "scale(0)",
                            },
                            "50%": {
                              transform: "scale(1)",
                            },
                          },
                        }}
                      />
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "text.secondary",
                          animation: "bounce 1.4s infinite ease-in-out both",
                          animationDelay: "0.2s",
                        }}
                      />
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "text.secondary",
                          animation: "bounce 1.4s infinite ease-in-out both",
                          animationDelay: "0.4s",
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Fade>

              {/* Invisible element for scroll to bottom */}
              <div ref={messageEndRef} />
            </Box>

            {/* Quick response chips */}
            <Collapse in={expandQuickResponses}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: "background.paper",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                {QUICK_RESPONSES.map((response, index) => (
                  <Chip
                    key={index}
                    label={response}
                    onClick={() => handleQuickResponse(response)}
                    clickable
                    sx={{
                      borderRadius: 2,
                      "&:hover": {
                        bgcolor: "primary.50",
                      },
                    }}
                  />
                ))}
              </Box>
            </Collapse>

            {/* Message Input */}
            <Box
              component="form"
              onSubmit={handleSendMessage}
              sx={{
                p: 2,
                borderTop: "1px solid",
                borderColor: "divider",
                bgcolor: "white",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <TextField
                fullWidth
                inputRef={inputRef}
                placeholder="Skriv ett meddelande..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                variant="outlined"
                size="small"
                disabled={isSending}
                InputProps={{
                  sx: {
                    borderRadius: 3,
                    "& fieldset": {
                      borderWidth: "1px",
                    },
                  },
                  endAdornment: (
                    <Tooltip title="Bifoga fil" arrow>
                      <IconButton
                        color="primary"
                        edge="end"
                        disabled={isSending}
                        sx={{
                          opacity: 0.7,
                          "&:hover": {
                            opacity: 1,
                          },
                        }}
                      >
                        <AttachIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ),
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />
              <IconButton
                type="submit"
                color="primary"
                disabled={!message.trim() || isSending}
                sx={{
                  bgcolor: "primary.main",
                  color: "white",
                  "&:hover": {
                    bgcolor: "primary.dark",
                  },
                  "&.Mui-disabled": {
                    bgcolor: "action.disabledBackground",
                    color: "action.disabled",
                  },
                  width: 40,
                  height: 40,
                }}
              >
                {isSending ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  <SendIcon />
                )}
              </IconButton>
            </Box>
          </>
        )}
      </ChatBox>
    );
  };

  return (
    <Box
      ref={widgetRef}
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 3,
      }}
    >
      <Zoom in={!isOpen}>
        <Badge
          color="error"
          badgeContent={unreadCount}
          overlap="circular"
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiBadge-badge": {
              animation: unreadCount > 0 ? "pulse 2s infinite" : "none",
              "@keyframes pulse": {
                "0%": {
                  transform: "scale(1)",
                  boxShadow: "0 0 0 0 rgba(244, 67, 54, 0.7)",
                },
                "70%": {
                  transform: "scale(1)",
                  boxShadow: "0 0 0 10px rgba(244, 67, 54, 0)",
                },
                "100%": {
                  transform: "scale(1)",
                  boxShadow: "0 0 0 0 rgba(244, 67, 54, 0)",
                },
              },
            },
          }}
        >
          <Fab
            color="primary"
            onClick={toggleWidget}
            aria-label="chat support"
            size={isMobile ? "small" : "medium"}
            sx={{
              background: "#489858",
              boxShadow: "0 4px 20px rgba(63, 81, 181, 0.25)",
              "&:hover": {
                background: "linear-gradient(45deg, #303f9f 0%, #3f51b5 100%)",
              },
            }}
          >
            <ChatIcon sx={{ fontSize: isMobile ? "1.2rem" : "1.4rem" }} />
          </Fab>
        </Badge>
      </Zoom>

      {/* Chat window */}
      {isOpen && renderChatWidget()}
    </Box>
  );
};

export default SupportWidget;
